import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  type SobrietyTracker, 
  getNextMilestone, 
  getReachedMilestones, 
  type MilestoneInfo 
} from "@/hooks/useSobrietyTracker";
import { Timer, RotateCcw, Trash2, Trophy, Euro, Clock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Props {
  tracker: SobrietyTracker;
  onReset: (id: string) => void;
  onDelete: (id: string) => void;
  compact?: boolean;
}

function useElapsedTime(startDate: string) {
  const [elapsed, setElapsed] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const rafRef = useRef<number>();

  useEffect(() => {
    const start = new Date(startDate).getTime();

    function update() {
      const now = Date.now();
      const diff = Math.max(0, now - start);
      const totalSeconds = Math.floor(diff / 1000);
      setElapsed({
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
      });
      rafRef.current = requestAnimationFrame(update);
    }

    update();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [startDate]);

  return elapsed;
}

function TimeUnit({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-3xl md:text-4xl font-bold tabular-nums text-foreground">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
        {label}
      </span>
    </div>
  );
}

export function SobrietyCounter({ tracker, onReset, onDelete, compact = false }: Props) {
  const elapsed = useElapsedTime(tracker.start_date);
  const nextMilestone = getNextMilestone(elapsed.days);
  const reached = getReachedMilestones(elapsed.days);
  const latestMilestone = reached.length > 0 ? reached[reached.length - 1] : null;

  const progressToNext = nextMilestone
    ? ((elapsed.days - (latestMilestone?.days ?? 0)) /
        (nextMilestone.days - (latestMilestone?.days ?? 0))) *
      100
    : 100;

  const moneySaved = elapsed.days * (tracker.daily_cost_euros || 0);
  const timeSaved = elapsed.days * (tracker.daily_time_minutes || 0);
  const hoursSaved = Math.floor(timeSaved / 60);

  if (compact) {
    return (
      <Card className="glass-card border-primary/20 overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Timer className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm">{tracker.substance}</span>
            </div>
            {latestMilestone && (
              <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                {latestMilestone.emoji} {latestMilestone.label}
              </Badge>
            )}
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold tabular-nums">{elapsed.days}</span>
            <span className="text-sm text-muted-foreground">jours</span>
            <span className="text-lg font-semibold tabular-nums ml-1">
              {String(elapsed.hours).padStart(2, "0")}:{String(elapsed.minutes).padStart(2, "0")}
            </span>
          </div>
          {moneySaved > 0 && (
            <p className="text-xs text-success mt-1 flex items-center gap-1">
              <Euro className="w-3 h-3" />
              {moneySaved.toFixed(0)}€ économisés
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card border-primary/20 overflow-hidden">
      <CardContent className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
              <Timer className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-lg">{tracker.substance}</h3>
              <p className="text-xs text-muted-foreground">
                Depuis le {new Date(tracker.start_date).toLocaleDateString("fr-FR")}
              </p>
            </div>
          </div>
          {latestMilestone && (
            <Badge className="bg-primary/15 text-primary border-primary/30 text-sm">
              {latestMilestone.emoji} {latestMilestone.label}
            </Badge>
          )}
        </div>

        {/* Counter */}
        <div className="flex items-center justify-center gap-3 py-4">
          <TimeUnit value={elapsed.days} label="jours" />
          <span className="text-2xl text-muted-foreground/40 font-light">:</span>
          <TimeUnit value={elapsed.hours} label="heures" />
          <span className="text-2xl text-muted-foreground/40 font-light">:</span>
          <TimeUnit value={elapsed.minutes} label="min" />
          <span className="text-2xl text-muted-foreground/40 font-light">:</span>
          <TimeUnit value={elapsed.seconds} label="sec" />
        </div>

        {/* Next milestone progress */}
        {nextMilestone && (
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Prochain : {nextMilestone.emoji} {nextMilestone.label}</span>
              <span>{Math.round(progressToNext)}%</span>
            </div>
            <Progress value={progressToNext} className="h-2" />
          </div>
        )}

        {/* Savings row */}
        {(moneySaved > 0 || hoursSaved > 0) && (
          <div className="flex gap-4">
            {moneySaved > 0 && (
              <div className="flex-1 rounded-xl bg-success/10 p-3 text-center">
                <Euro className="w-4 h-4 text-success mx-auto mb-1" />
                <p className="text-lg font-bold text-success">{moneySaved.toFixed(0)}€</p>
                <p className="text-[10px] text-muted-foreground">économisés</p>
              </div>
            )}
            {hoursSaved > 0 && (
              <div className="flex-1 rounded-xl bg-primary/10 p-3 text-center">
                <Clock className="w-4 h-4 text-primary mx-auto mb-1" />
                <p className="text-lg font-bold text-primary">{hoursSaved}h</p>
                <p className="text-[10px] text-muted-foreground">récupérées</p>
              </div>
            )}
          </div>
        )}

        {/* Milestones reached */}
        {reached.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Trophy className="w-3.5 h-3.5" />
              <span>Milestones atteints</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {reached.map((m) => (
                <Badge
                  key={m.days}
                  variant="outline"
                  className="text-xs border-primary/20 bg-primary/5"
                >
                  {m.emoji} {m.label}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="flex-1 text-warning border-warning/30">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                Reset
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remettre le compteur à zéro ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Ce n'est pas un échec. La rechute fait partie du parcours.
                  Ton prochain départ commence maintenant. 💛
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={() => onReset(tracker.id)}>
                  Recommencer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-destructive">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer ce compteur ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Cette action est irréversible. Tout l'historique de ce compteur sera perdu.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(tracker.id)}
                  className="bg-destructive text-destructive-foreground"
                >
                  Supprimer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}
